import { observable, action, runInAction } from 'mobx'
import { notification as notifApi } from '../api/hokan'
import * as NotifTypes from '../types/notification'
import { errorMessage, mapPaginationMeta } from '../components/notification/shared'

export class NotificationAdminStore {
    @observable public list: NotifTypes.NotificationSummary[] = []
    @observable public pagination: NotifTypes.PaginationMeta = { current_page: 1, total_pages: 1, total_count: 0 }
    @observable public currentItem: NotifTypes.NotificationDetail | null = null
    @observable public loading: boolean = false
    @observable public error: string | null = null
    @observable public statusFilter: NotifTypes.NotificationStatus | '' = ''
    @observable public priorityFilter: NotifTypes.NotificationPriority | '' = ''
    @observable public typeFilter: NotifTypes.NotificationType | '' = ''
    @observable public titleQuery: string = ''

    // Optimistic "just fired" overlay. send_now / update(send_mode=now) trigger a
    // background fire job that does NOT flip status synchronously, so a refetch
    // (list mount or 15s poll) can still report draft/scheduled for a moment.
    // Track recently-fired ids -> mark them sent on every fetch until the server
    // confirms a terminal status, or the grace window lapses (so a job that truly
    // failed eventually surfaces instead of being pinned to "sent" forever).
    private optimisticSentAt: { [id: number]: number } = {}
    private static OPTIMISTIC_SENT_TTL_MS = 65000

    // Record a fire and immediately reflect it in the current list.
    @action
    public markOptimisticSent(id: number) {
        this.optimisticSentAt[id] = Date.now()
        this.applyOptimisticSent()
    }

    // Overlay recently-fired ids onto this.list. MUST be called from within an
    // action / runInAction (it reassigns this.list). Drops an id once the server
    // reports a terminal status, or once the grace window has elapsed.
    private applyOptimisticSent() {
        if (Object.keys(this.optimisticSentAt).length === 0) return
        const now = Date.now()
        this.list = this.list.map(n => {
            const firedAt = this.optimisticSentAt[n.id]
            if (firedAt == null) return n
            const serverSettled = n.status === 'sent' || n.status === 'canceled' || n.status === 'expired'
            const expired = now - firedAt >= NotificationAdminStore.OPTIMISTIC_SENT_TTL_MS
            if (serverSettled || expired) {
                delete this.optimisticSentAt[n.id]
                return n
            }
            const firedIso = new Date(firedAt).toISOString()
            return {
                ...n,
                status: 'sent' as NotifTypes.NotificationStatus,
                scheduled_at: n.scheduled_at || firedIso,
                sent_at: n.sent_at || firedIso,
            }
        })
    }

    @action
    public setStatusFilter(s: NotifTypes.NotificationStatus | '') {
        this.statusFilter = s
    }

    @action
    public setPriorityFilter(p: NotifTypes.NotificationPriority | '') {
        this.priorityFilter = p
    }

    @action
    public setTypeFilter(t: NotifTypes.NotificationType | '') {
        this.typeFilter = t
    }

    @action
    public setTitleQuery(q: string) {
        this.titleQuery = q
    }

    @action
    public resetFilters() {
        this.statusFilter = ''
        this.priorityFilter = ''
        this.typeFilter = ''
        this.titleQuery = ''
    }

    @action
    public async fetchList(page: number = 1) {
        this.loading = true
        this.error = null
        try {
            const params: NotifTypes.NotificationListParams & { page: number; per_page: number } = {
                page,
                per_page: 20,
            }
            if (this.statusFilter) {
                params.status = this.statusFilter
            }
            if (this.priorityFilter) {
                params.priority = this.priorityFilter
            }
            if (this.typeFilter) {
                params.type = this.typeFilter
            }
            const trimmedQ = this.titleQuery.trim()
            if (trimmedQ) {
                params.q = trimmedQ
            }
            const res = await notifApi.list(params)
            runInAction(() => {
                this.list = res.data.data
                this.pagination = mapPaginationMeta(res.data.pagination)
                this.applyOptimisticSent()
            })
        } catch (e) {
            runInAction(() => {
                this.error = errorMessage(e, 'Failed to load')
            })
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }

    @action
    public async fetchOne(id: number) {
        this.loading = true
        try {
            const res = await notifApi.get(id)
            runInAction(() => {
                // BE wraps the record as { notification: {...} } — unwrap it.
                this.currentItem = res.data.notification
            })
        } catch (e) {
            runInAction(() => {
                this.error = errorMessage(e, 'Failed to load')
            })
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }

    @action
    public async create(payload: NotifTypes.CreateNotificationPayload): Promise<NotifTypes.NotificationDetail> {
        this.loading = true
        try {
            const res = await notifApi.create(payload)
            return res.data.notification
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }

    @action
    public async update(id: number, payload: NotifTypes.CreateNotificationPayload): Promise<NotifTypes.NotificationDetail> {
        this.loading = true
        try {
            const res = await notifApi.update(id, payload)
            if (payload.send_mode === 'now') {
                this.markOptimisticSent(id)
            }
            return res.data.notification
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }

    @action
    public async sendNow(id: number) {
        try {
            await notifApi.sendNow(id)
            this.markOptimisticSent(id)
        } catch (e) {
            // 422 on status means it already left the sendable state (race with
            // the scheduler/another admin) — just resync the list, not an error.
            const resp = e && (e as any).response
            const statusErr = resp && resp.status === 422 &&
                resp.data && resp.data.errors && resp.data.errors.status
            if (statusErr) {
                await this.fetchList(this.pagination.current_page)
                return
            }
            runInAction(() => {
                this.error = errorMessage(e, 'Failed to send')
            })
        }
    }

    @action
    public async cancel(id: number) {
        try {
            await notifApi.cancel(id)
            await this.fetchList(this.pagination.current_page)
        } catch (e) {
            runInAction(() => {
                this.error = errorMessage(e, 'Failed to cancel')
            })
        }
    }

    @action
    public async destroy(id: number) {
        try {
            await notifApi.destroy(id)
            const { current_page, total_count } = this.pagination
            const remaining = total_count - 1
            const lastPage = Math.max(1, Math.ceil(remaining / 20))
            const target = Math.min(current_page, lastPage)
            await this.fetchList(target)
        } catch (e) {
            runInAction(() => {
                this.error = errorMessage(e, 'Failed to delete')
            })
        }
    }
}

export default NotificationAdminStore
